import { ReactNode } from 'react';
import Link from 'next/link';

export default function AuthShell({
  title,
  subtitle,
  children,
  altLink,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  altLink?: { label: string; href: string; text: string };
}) {
  return (
    <section className="auth-shell">
      <div className="auth-card">
        <h1 className="auth-title">{title}</h1>
        {subtitle && <p className="auth-sub">{subtitle}</p>}
        {children}
        {altLink && (
          <p className="auth-alt">
            {altLink.text}{' '}
            <Link href={altLink.href} className="auth-alt-link">
              {altLink.label}
            </Link>
          </p>
        )}
      </div>
      <style>{`
        .auth-shell {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem 1rem;
          background: var(--color-off-white);
        }
        .auth-card {
          width: 100%;
          max-width: 420px;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 2rem 1.75rem;
          box-shadow: var(--shadow-sm);
        }
        .auth-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.4rem;
        }
        .auth-sub {
          font-size: 0.9rem;
          color: var(--color-text-secondary);
          margin: 0 0 1.5rem;
          line-height: 1.45;
        }
        .auth-alt {
          text-align: center;
          font-size: 0.85rem;
          color: var(--color-text-secondary);
          margin: 1.5rem 0 0;
          padding-top: 1.25rem;
          border-top: 1px solid var(--color-border);
        }
        .auth-alt-link {
          color: var(--color-primary);
          font-weight: 600;
          text-decoration: none;
        }
        .auth-alt-link:hover { text-decoration: underline; }
        .auth-field {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          margin-bottom: 1rem;
        }
        .auth-field-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .auth-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .auth-input {
          width: 100%;
          padding: 0.7rem 0.85rem;
          font-size: 0.95rem;
          font-family: inherit;
          border: 1px solid var(--color-border);
          border-radius: 0.5rem;
          background: var(--color-white);
          color: var(--color-text-primary);
          transition: border-color 0.15s, box-shadow 0.15s;
          box-sizing: border-box;
          line-height: 1.3;
        }
        .auth-input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px rgba(27,63,35,0.12);
        }
        .auth-error {
          background: #fdecee;
          color: #a1272c;
          border: 1px solid #f5c2c6;
          padding: 0.65rem 0.8rem;
          border-radius: 0.5rem;
          font-size: 0.85rem;
          margin-bottom: 1rem;
        }
        .auth-success {
          background: #eaf5ee;
          color: #1b5a30;
          border: 1px solid #bfe0ca;
          padding: 0.65rem 0.8rem;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          margin-bottom: 1rem;
          line-height: 1.5;
        }
        .auth-button {
          display: block;
          width: 100%;
          padding: 0.8rem 1rem;
          font-size: 0.95rem;
          font-weight: 600;
          font-family: inherit;
          background: var(--color-primary);
          color: var(--color-white);
          border: none;
          border-radius: 1rem;
          cursor: pointer;
          transition: background 0.15s, opacity 0.15s;
        }
        .auth-button:hover { background: var(--color-primary-dark); }
        .auth-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .auth-forgot {
          display: block;
          text-align: right;
          font-size: 0.8rem;
          color: var(--color-primary);
          text-decoration: none;
          margin: -0.25rem 0 1rem;
        }
        .auth-forgot:hover { text-decoration: underline; }
      `}</style>
    </section>
  );
}
